// VIBRANIAM-SPACE v2 - evaluate.js
// Topological evaluation of the signal graph, plus resolution of every
// parameter through its modulation slots.
//
// The runtime keeps per-node state across frames (smoothers, envelopes, LFO
// phase) and nothing else. Given the same graph and the same audio, stepping
// from frame 0 always produces the same values.

import { NODE_TYPES, isSignalNode, schemaOf } from './nodes/index.js';
import { applyCurve, clamp, coerce, defaultForKind, EMPTY_ARRAY } from './types.js';

/** Deterministic hash-based random in [0,1). Same inputs, same output, always. */
export function seededRandom(a, b) {
  let h = (Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca77)) >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x846ca68b) >>> 0;
  h ^= h >>> 16;
  return (h & 0x00ffffff) / 0x1000000;
}

export function topoOrder(nodes, edges) {
  const indeg = new Map(nodes.map((n) => [n.id, 0]));
  const adj = new Map(nodes.map((n) => [n.id, []]));
  for (const e of edges) {
    if (!indeg.has(e.to.node) || !adj.has(e.from.node)) continue;
    indeg.set(e.to.node, indeg.get(e.to.node) + 1);
    adj.get(e.from.node).push(e.to.node);
  }
  const queue = nodes.filter((n) => indeg.get(n.id) === 0).map((n) => n.id);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    for (const next of adj.get(id) || []) {
      indeg.set(next, indeg.get(next) - 1);
      if (indeg.get(next) === 0) queue.push(next);
    }
  }
  return order.length === nodes.length ? order : null;
}

export class GraphRuntime {
  constructor() {
    this.graph = { nodes: [], edges: [] };
    this.order = [];
    this.cyclic = false;
    this.states = new Map();   // nodeId -> node state
    this.values = new Map();   // "nodeId:portId" -> value
    this.byId = new Map();
    this.incoming = new Map(); // "nodeId:portId" -> [edge]
    // Track last graph's node and edge IDs to avoid redundant topoOrder recomputation
    this.lastNodeIds = new Set();
    this.lastEdgeIds = new Set();
  }

  setGraph(graph, order) {
    this.graph = graph;
    this.byId = new Map(graph.nodes.map((n) => [n.id, n]));

    // Drop state for nodes that no longer exist.
    for (const id of [...this.states.keys()]) {
      if (!this.byId.has(id)) this.states.delete(id);
    }
    for (const n of graph.nodes) {
      const def = NODE_TYPES[n.type];
      if (def?.state && !this.states.has(n.id)) this.states.set(n.id, def.state());
    }

    this.incoming = new Map();
    for (const e of graph.edges) {
      const key = `${e.to.node}:${e.to.port}`;
      if (!this.incoming.has(key)) this.incoming.set(key, []);
      this.incoming.get(key).push(e);
    }

    // Check if we can reuse the previous order to avoid recomputing topoOrder
    const currentNodeIds = new Set(graph.nodes.map(n => n.id));
    const currentEdgeIds = new Set(graph.edges.map(e => `${e.from.node}:${e.from.port}:${e.to.node}:${e.to.port}`));

    const nodeIdsSame = currentNodeIds.size === this.lastNodeIds.size &&
                       [...currentNodeIds].every(id => this.lastNodeIds.has(id));
    const edgeIdsSame = currentEdgeIds.size === this.lastEdgeIds.size &&
                       [...currentEdgeIds].every(id => this.lastEdgeIds.has(id));

    if (order !== undefined) {
      // Use provided order (from useModuleGraph)
      this.order = order;
      this.cyclic = false;
    } else if (nodeIdsSame && edgeIdsSame) {
      // Reuse previous order if graph structure hasn't changed
      this.cyclic = false;
    } else {
      // Recompute topoOrder
      const computedOrder = topoOrder(graph.nodes, graph.edges);
      this.cyclic = computedOrder === null;
      this.order = computedOrder || graph.nodes.map((n) => n.id);
    }

    // Update tracked IDs for next comparison
    this.lastNodeIds = currentNodeIds;
    this.lastEdgeIds = currentEdgeIds;
  }

  resetState() {
    for (const n of this.graph.nodes) {
      const def = NODE_TYPES[n.type];
      this.states.set(n.id, def?.state ? def.state() : {});
    }
    this.values.clear();
  }

  edgesInto(nodeId, portId) {
    return this.incoming.get(`${nodeId}:${portId}`) || [];
  }

  /** Raw value on a source port, from the last evaluation. */
  portValue(nodeId, portId) {
    const v = this.values.get(`${nodeId}:${portId}`);
    return v === undefined ? 0 : v;
  }

  /**
   * One control-rate step. `ctx` carries features, dt, frame and fps.
   * Only signal nodes are evaluated here; render nodes are read by the Engine.
   */
  evaluate(ctx) {
    if (this.cyclic) return;
    const c = { ...ctx, rand: (a, b) => seededRandom(a, b) };

    for (const id of this.order) {
      const node = this.byId.get(id);
      if (!node || !isSignalNode(node.type)) continue;
      const def = NODE_TYPES[node.type];

      // Gather declared inputs.
      const inp = {};
      for (const port of def.inputs || []) {
        const edges = this.edgesInto(id, port.id);
        if (!edges.length) { inp[port.id] = defaultForKind(port.kind); continue; }
        // A declared input takes the first cable; parameter ports sum instead.
        const e = edges[0];
        inp[port.id] = coerce(this.portValue(e.from.node, e.from.port), port.kind);
      }

      const params = this.resolveParams(node);
      const state = this.states.get(id) || {};
      let out;
      try {
        out = def.eval ? def.eval(inp, params, c, state) : {};
      } catch {
        out = {};
      }
      for (const port of def.outputs || []) {
        const v = out && out[port.id] !== undefined ? out[port.id] : defaultForKind(port.kind);
        this.values.set(`${id}:${port.id}`, v);
      }
    }
  }

  /**
   * base value + every modulation slot wired into `p:<key>`.
   * `amount` is a fraction of the parameter's full range, which is what makes
   * one slider feel the same as any other.
   */
  resolveParams(node) {
    const fields = schemaOf(node);
    const out = { ...node.params };

    for (const f of fields) {
      if (!f.mod) continue;
      const edges = this.edgesInto(node.id, `p:${f.key}`);
      if (!edges.length) continue;

      const range = (f.max ?? 1) - (f.min ?? 0);
      let v = Number(node.params[f.key]);
      if (!Number.isFinite(v)) v = f.min ?? 0;

      for (const e of edges) {
        const raw = this.portValue(e.from.node, e.from.port);
        const s = typeof raw === 'boolean' ? (raw ? 1 : 0)
                : typeof raw === 'number' ? raw
                : (raw && raw.length ? raw[0] : 0);
        const amount = Number.isFinite(e.amount) ? e.amount : 1;
        const sign = e.polarity === 'neg' ? -1 : 1;
        v += applyCurve(s, e.curve || 'lin') * amount * range * sign;
      }
      out[f.key] = clamp(v, f.min ?? -Infinity, f.max ?? Infinity);
    }
    return out;
  }

  /** Every node of a type, in graph order. */
  nodesOfType(type) {
    return this.graph.nodes.filter((n) => n.type === type);
  }

  /** Source nodes feeding a multi-input port, ordered left-to-right on canvas. */
  sourcesInto(nodeId, portId) {
    return this.edgesInto(nodeId, portId)
      .map((e) => this.byId.get(e.from.node))
      .filter(Boolean)
      .sort((a, b) => a.x - b.x);
  }
}

export const EMPTY_FEATURES = Object.freeze({
  bands: EMPTY_ARRAY,
  spectrum: EMPTY_ARRAY,
  rms: 0, peak: 0, flux: 0, onset: false,
  centroid: 0, dominantFreq: 0,
  bass: 0, mid: 0, high: 0,
  sampleRate: 48000, binCount: 0,
});
